import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { isCode, messageFor, post, type BookEntity, type EventRow } from "../api";
import { ENTITIES, factoryName, nowEntityIso, qty } from "../format";

type DisposalIn = {
  event_id: string;
  event_type: "disposal";
  entity: BookEntity;
  sku: string;
  factory: string;
  qty: string;
  reference: string | null;
  occurred_at: string;
  note: string;
};

export function DisposeFlow({ entity, sku, uom, holdings, open, onOpenChange }: {
  entity: BookEntity;
  sku: string;
  uom: "kg" | "unit";
  holdings: Array<{ id: string; qty: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const client = useQueryClient();
  const [factory, setFactory] = useState(holdings[0]?.id ?? "");
  const [quantity, setQuantity] = useState("");
  const [reasonKind, setReasonKind] = useState<"supplier" | "other">("supplier");
  const [creditRef, setCreditRef] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState<DisposalIn | null>(null);
  const [step, setStep] = useState<"entry" | "preview" | "success">("entry");
  const previewRef = useRef<HTMLDivElement>(null);
  const located = holdings.find((item) => item.id === factory)?.qty ?? "0";
  const number = Number(quantity);
  const quantityProblem = quantity.trim() === "" ? null : !Number.isFinite(number) || number <= 0 ? "Enter a quantity above zero." : number > Number(located) ? `Only ${located} at ${factoryName(factory)}.` : null;
  const note = reasonKind === "supplier" ? "Returned to supplier" : reason.trim();
  const canPreview = quantity.trim() !== "" && !quantityProblem && factory !== "" && (reasonKind === "supplier" ? creditRef.trim() !== "" : reason.trim() !== "");
  const mutation = useMutation({
    mutationFn: (body: DisposalIn) => post<EventRow>("/events", body),
    onSuccess: () => {
      setStep("success");
      void client.invalidateQueries({ queryKey: ["raw_materials"] });
    },
  });
  const authExpired = isCode(mutation.error, "no_token") || isCode(mutation.error, "invalid_token");
  function preview() {
    setSubmitted({
      event_id: crypto.randomUUID(),
      event_type: "disposal",
      entity,
      sku,
      factory,
      qty: `-${quantity.trim()}`,
      reference: reasonKind === "supplier" ? creditRef.trim() : null,
      occurred_at: nowEntityIso(entity),
      note,
    });
    setStep("preview");
  }
  useEffect(() => { if (step === "preview") previewRef.current?.focus(); }, [step]);

  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent><DialogHeader><DialogTitle>Remove {sku} from the pool</DialogTitle><DialogDescription>{ENTITIES[entity].name}. Record stock that will not be used in production.</DialogDescription></DialogHeader>
    {step === "entry" && <div className="space-y-4"><div className="grid gap-3 sm:grid-cols-2"><div className="space-y-1"><Label htmlFor="dispose-factory">Factory</Label><Select value={factory} onValueChange={setFactory}><SelectTrigger id="dispose-factory" className="w-full"><SelectValue /></SelectTrigger><SelectContent>{holdings.map((item) => <SelectItem key={item.id} value={item.id}>{factoryName(item.id)} ({qty(item.qty, uom)})</SelectItem>)}</SelectContent></Select></div><div className="space-y-1"><Label htmlFor="dispose-qty">Quantity</Label><Input id="dispose-qty" inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} />{quantityProblem && <span className="text-xs text-ks-danger">{quantityProblem}</span>}</div></div>
      <div className="space-y-2"><Label>Reason</Label><RadioGroup value={reasonKind} onValueChange={(value) => setReasonKind(value as "supplier" | "other")}><label className="flex items-start gap-2"><RadioGroupItem value="supplier" /><span className="grow">Returned to supplier<Input className="mt-2" aria-label="Credit-note reference" placeholder="Credit-note reference" value={creditRef} onChange={(event) => setCreditRef(event.target.value)} /></span></label><label className="flex items-start gap-2"><RadioGroupItem value="other" /><span className="grow">Other reason<Textarea className="mt-2" aria-label="Removal reason" value={reason} onChange={(event) => setReason(event.target.value)} /></span></label></RadioGroup></div>
      <DialogFooter><Button disabled={!canPreview} onClick={preview}>Preview log entry →</Button></DialogFooter></div>}
    {step === "preview" && submitted && <div ref={previewRef} tabIndex={-1} className="space-y-4"><p className="rounded-ks-md border border-ks-grey-4 p-4">- {quantity} {sku} from {factoryName(factory)}</p>{mutation.error && <Alert variant="destructive"><AlertDescription>{authExpired ? <>Your sign-in has expired. Reload the page to sign in again; unsaved values in this form will be lost. <Button data-nonmutating variant="outline" size="sm" onClick={() => window.location.reload()}>Reload</Button></> : messageFor(mutation.error)}</AlertDescription></Alert>}<DialogFooter><Button data-nonmutating variant="outline" onClick={() => setStep("entry")}>Back</Button><Button disabled={mutation.isPending || authExpired} onClick={() => mutation.mutate(submitted)}>Confirm — remove from pool</Button></DialogFooter></div>}
    {step === "success" && <div className="space-y-4"><p role="status" className="font-bold text-ks-ink">Removed {qty(Math.abs(Number(mutation.data?.data.qty_delta ?? 0)), uom)} of {mutation.data?.data.xero_item_code}.</p><DialogFooter><Button data-nonmutating onClick={() => onOpenChange(false)}>Close</Button></DialogFooter></div>}
  </DialogContent></Dialog>;
}
